import { useCallback, useState, useEffect } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useTsrClient } from '../../end-users/tsr'
import { Card, CardContent, CardHeader, CardTitle } from '@alphabait/ui' // card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@alphabait/ui' // table'
import { Button } from '@alphabait/ui'
import { Loader2, RotateCcw } from 'lucide-react'
import { CampaignQuestionVisualizer } from './campaign-question-visualizer'
import { GroupedQuestionVisualizer } from './grouped-question-visualizer'
import { Switch } from '@/components/switch'
import { ChartLoadingState } from '@/components/charts/question-renderers/shared/chart-loading-state'
const questionTypeLabels: Record<string, string> = {
  input: 'questionnaire_builder.text_field',
  email: 'questionnaire_builder.email',
  textarea: 'questionnaire_builder.long_text_field',
  'shadcn-select': 'questionnaire_builder.dropdown_field',
  select: 'questionnaire_builder.dropdown_field',
  'picture-choice': 'questionnaire_builder.picture_choice',
  pictureChoice: 'questionnaire_builder.picture_choice',
  radioGroup: 'questionnaire_builder.dynamic_radio_group',
  radioGroupGender: 'questionnaire_builder.radio_field',
  dateInput: 'questionnaire_builder.date_field',
  switch: 'questionnaire_builder.toggle_field',
  checkbox: 'questionnaire_builder.checkbox_field',
  'multi-select': 'questionnaire_builder.dynamic_radio_group',
  multiSelect: 'questionnaire_builder.dynamic_radio_group',
}

import {
  ChartEmptyState,
  ChartErrorState,
} from '@/components/charts/question-renderers/shared'

interface CampaignQuestionsProps {
  className?: string
  periodStart: string
  periodEnd: string
  customerId?: string
  campaignIds?: string[]
  campaignGroupId?: string
  questionContext?: 'all' | 'standard' | 'referral'
  onCampaignSelect?: (campaignId: string) => void
}

export function CampaignQuestions({
  className,
  periodStart,
  periodEnd,
  customerId,
  campaignIds,
  campaignGroupId,
  questionContext = 'all',
  onCampaignSelect,
}: CampaignQuestionsProps) {
  const { t } = useTranslation()
  const tsr = useTsrClient()
  const limit = 10
  const [selectedQuestions, setSelectedQuestions] = useState<
    Map<
      string,
      {
        id: string
        name: string
        type: string
        text: string
        campaignIds: string[]
        campaignNames: string[]
      }
    >
  >(new Map())

  // Use infinite query for campaign questions with pagination
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    error: questionsError,
  } = useInfiniteQuery({
    queryKey: [
      'campaignQuestions',
      periodStart,
      periodEnd,
      customerId,
      campaignIds,
      campaignGroupId,
      questionContext,
    ],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await tsr.dashboard.getCampaignQuestions.query({
        query: {
          page: pageParam.toString(),
          limit: limit.toString(),
          periodStart,
          periodEnd,
          customer_id: customerId,
          campaignIds,
          campaignGroupId,
          questionContext,
        },
      })

      if (!response.status || response.status !== 200) {
        throw new Error('Failed to fetch campaign questions')
      }
      return response.body
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.totalPages) {
        return lastPage.page + 1
      }
      return undefined
    },
  })

  // Flatten all pages into a single array (with null-safe handling)
  const questions = data?.pages.flatMap((page) => page.data ?? []) ?? []
  const totalCount = data?.pages[0]?.totalCount ?? 0

  // Get campaign IDs to filter summary:
  // - When questions are selected: use the selected questions' campaign IDs
  // - Otherwise: use the page's campaignIds prop (from URL filter)
  const selectedQuestionCampaignIds = Array.from(
    new Set(
      Array.from(selectedQuestions.values()).flatMap((q) => q.campaignIds),
    ),
  )
  const effectiveCampaignIds =
    selectedQuestionCampaignIds.length > 0
      ? selectedQuestionCampaignIds
      : campaignIds

  // Use ts-rest client for campaign summary - filter by campaigns
  const {
    data: summaryData,
    isPending: summaryLoading,
    error: _summaryError,
  } = tsr.dashboard.getCampaignSummary.useQuery({
    queryKey: [
      'campaignSummary',
      periodStart,
      periodEnd,
      customerId,
      effectiveCampaignIds,
    ],
    queryData: {
      query: {
        periodStart,
        periodEnd,
        customer_id: customerId,
        campaignIds:
          effectiveCampaignIds && effectiveCampaignIds.length > 0
            ? effectiveCampaignIds.join(',')
            : undefined,
      },
    },
  })

  const summary = summaryData?.body || null

  // Handle checkbox toggle for questions (now with grouped campaigns)
  const handleQuestionToggle = (
    question: {
      question_id?: string
      question_name?: string
      question_type?: string
      question_text?: string
      campaigns?: Array<{
        campaign_id: string
        campaign_name: string
        campaign_status: string
        customer_id: string
        customer_name: string
      }>
    } | null,
    checked: boolean,
  ) => {
    if (!question) return
    const newSelected = new Map(selectedQuestions)
    const questionId = question.question_id || ''

    if (checked) {
      const campaigns = question.campaigns || []
      newSelected.set(questionId, {
        id: questionId,
        name: question.question_name || '',
        type: question.question_type || '',
        text: question.question_text || '',
        campaignIds: campaigns.map((c) => c.campaign_id),
        campaignNames: campaigns.map((c) => c.campaign_name),
      })
    } else {
      newSelected.delete(questionId)
    }
    setSelectedQuestions(newSelected)
  }

  // Handle reset all selections
  const handleResetSelection = () => {
    setSelectedQuestions(new Map())
  }

  // Reset selection when filters change to prevent stale/mismatched widget data
  useEffect(() => {
    setSelectedQuestions(new Map())
  }, [
    periodStart,
    periodEnd,
    customerId,
    campaignIds,
    campaignGroupId,
    questionContext,
  ])

  // Handle scroll-based infinite loading
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget

      // Load more when user scrolls to within 100px of bottom
      if (
        scrollHeight - scrollTop <= clientHeight + 100 &&
        hasNextPage &&
        !isFetchingNextPage
      ) {
        fetchNextPage()
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  )

  return (
    <>
      <Card className={className}>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{t('dashboard.campaign_questions.title')}</CardTitle>

          <Button
            variant="outline"
            size="sm"
            onClick={handleResetSelection}
            className="gap-2 rounded-full"
            disabled={selectedQuestions.size === 0}
          >
            <RotateCcw className="h-4 w-4" />
            {t('dashboard.campaign_questions.reset_selection')}
          </Button>
        </CardHeader>
        <CardContent>
          {status === 'pending' ? (
            <ChartLoadingState />
          ) : status === 'error' ? (
            <ChartErrorState
              message={t('dashboard.charts.failed')}
              error={questionsError?.message}
            />
          ) : questions.length === 0 ? (
            <div className="flex justify-center items-center">
              <ChartEmptyState
                message={t('dashboard.campaign_questions.no_questions_found')}
              />
            </div>
          ) : (
            <>
              <div className="flex justify-between mb-2">
                {/* Summary Statistics - show selection info when questions selected, otherwise global summary */}
                <div className="mb-2">
                  {summaryLoading ? (
                    <div className="flex items-center gap-2">
                      <ChartLoadingState
                        message={t(
                          'dashboard.campaign_questions.loading_summary',
                        )}
                      />
                    </div>
                  ) : summary ? (
                    <div className="text-sm text-marine-500">
                      <span className="font-medium text-marine-500">
                        {t('dashboard.campaign_questions.campaigns_label', {
                          count: summary.totalCampaigns,
                        })}{' '}
                        |{' '}
                      </span>
                      <span className="font-medium text-marine-500">
                        {t(
                          'dashboard.campaign_questions.avg_completion_label',
                          {
                            rate: summary.avgCompletionRate,
                          },
                        )}{' '}
                        |{' '}
                      </span>
                      <span className="font-medium text-marine-500">
                        {t('dashboard.campaign_questions.total_users_label', {
                          count: summary.totalUsers,
                        })}
                      </span>
                    </div>
                  ) : (
                    <div className="text-sm">
                      {t('dashboard.campaign_questions.unable_to_load')}
                    </div>
                  )}
                </div>
                <div className="text-sm text-muted-foreground font-light">
                  {t('dashboard.campaign_questions.loaded_questions', {
                    loaded: questions.length,
                    total: totalCount,
                  })}
                </div>
              </div>

              <div
                className="overflow-auto max-h-[500px]"
                onScroll={handleScroll}
              >
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-20 font-bold">
                      <TableHead className="w-12 rounded-tl-xl text-gray-30">
                        {t('dashboard.campaign_questions.select_header')}
                      </TableHead>
                      <TableHead className="text-gray-30">
                        {t('dashboard.campaign_questions.question_header')}
                      </TableHead>
                      <TableHead className="text-gray-30 max-w-40">
                        {t('dashboard.campaign_questions.type_header')}
                      </TableHead>
                      <TableHead className="text-gray-30">
                        {t('dashboard.campaign_questions.customer_header')}
                      </TableHead>
                      <TableHead className="rounded-tr-xl text-gray-30">
                        {t('dashboard.campaign_questions.campaign_header')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody className="text-gray-30">
                    {questions.map(
                      (question, index) =>
                        question && (
                          <TableRow
                            key={`${question.question_id || ''}-${index}`}
                          >
                            <TableCell>
                              <Switch
                                checked={selectedQuestions.has(
                                  question.question_id || '',
                                )}
                                onCheckedChange={(checked) =>
                                  handleQuestionToggle(
                                    question,
                                    checked === true,
                                  )
                                }
                              />
                            </TableCell>
                            <TableCell className="w-96 max-w-1/3">
                              {question.question_text || ''}
                            </TableCell>
                            <TableCell>
                              <span className="text-xs border px-2 py-1 rounded-full font-bold">
                                {questionTypeLabels[question.question_type]
                                  ? t(
                                      questionTypeLabels[
                                        question.question_type
                                      ],
                                    )
                                  : question.question_type ||
                                    t(
                                      'dashboard.campaign_questions.unknown_type',
                                    )}
                              </span>
                            </TableCell>
                            <TableCell className="font-bold">
                              {/* Show unique customer names from all campaigns */}
                              {[
                                ...new Set(
                                  question.campaigns?.map(
                                    (c) => c.customer_name,
                                  ) || [],
                                ),
                              ].join(', ') || ''}
                            </TableCell>
                            <TableCell className="w-80 max-w-1/3">
                              {/* Show campaign names as clickable links */}
                              <div className="grid flex-wrap gap-x-1 gap-y-2">
                                {question.campaigns?.map((campaign) => (
                                  <button
                                    key={campaign.campaign_id}
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      onCampaignSelect?.(campaign.campaign_id)
                                    }}
                                    className="text-primary hover:underline cursor-pointer font-medium  text-left"
                                  >
                                    {campaign.campaign_name}
                                  </button>
                                ))}
                              </div>
                            </TableCell>
                          </TableRow>
                        ),
                    )}
                    {/* Loading indicator for infinite scroll */}
                    {isFetchingNextPage && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center p-4">
                          <div className="flex justify-center">
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                    {hasNextPage && !isFetchingNextPage && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center p-2">
                          <span className="text-xs text-muted-foreground">
                            {t('dashboard.campaign_questions.scroll_for_more')}
                          </span>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Dynamic Visualizations Based on Selected Questions */}
      {selectedQuestions.size > 0 && (
        <div className="mt-6 space-y-2 bg-gray-8 rounded-md pb-4">
          <div className="space-y-8">
            {campaignGroupId ? (
              /* Campaign group selected: show single aggregated visualization */
              <GroupedQuestionVisualizer
                campaignGroupId={campaignGroupId}
                selectedQuestionIds={Array.from(selectedQuestions.keys())}
                questionContext={questionContext}
              />
            ) : (
              /* Individual campaigns: show per-campaign visualizations */
              Array.from(
                new Set(
                  Array.from(selectedQuestions.values()).flatMap(
                    (q) => q.campaignIds,
                  ),
                ),
              ).map((campaignId) => {
                const selectedQuestionIds = Array.from(
                  selectedQuestions.values(),
                )
                  .filter((q) => q.campaignIds.includes(campaignId))
                  .map((q) => q.id)

                return (
                  <div key={campaignId} className="space-y-4 ">
                    <CampaignQuestionVisualizer
                      campaignId={campaignId}
                      selectedQuestionIds={selectedQuestionIds}
                      questionContext={questionContext}
                    />
                  </div>
                )
              })
            )}
          </div>
        </div>
      )}
    </>
  )
}
